/**
 * Parameters for Design Materials - physically based mesh materials that have procedurally defined detailing.
 * @module
 */
import { spliceBitmask } from "common/utils/bitfield";
import degreeToRadian from "common/utils/degreeToRadian";
import { closestIntegerPowerOfTwo, fmod } from "common/utils/math";
import { schemaVersionLessThanOrEqualTo } from "common/utils/schemaVersion";
import type { Mat3 } from "geom/mat3";
import { mat3Mult, mat3Rotate, mat3Scale, mat3Translate } from "geom/mat3";
import type { Matrix3, MeshPhysicalMaterial, MeshStandardMaterial } from "three";
import { Color, SRGBColorSpace } from "three";
import {
  CONTINUOUS_HATCH_PATTERN_PERIOD,
  defaultLayerConfigParams,
  defaultLayerParams,
  defaultMaskParams,
  defaultSdfPattern,
  MAX_SDF_PATTERNS,
  NUM_LAYERS,
} from "./designMaterialDefaults";
import { designMaterialMigrations } from "./designMaterialMigrations";
import type {
  LayerConfigLibraryParams,
  ColorLibraryParams,
  MaskParams,
  SdfCommonParamsPreprocessed,
} from "./designMaterialTypes";
import type {
  DesignMaterialParams,
  DmRoundSidesMode,
  LayerConfigParams,
  LayerParams,
  SdfCommonParams,
  SdfDistortion,
  DesignMaterialParamsPreprocessed,
  LayerParamsPreprocessed,
  SdfPatternPreprocessed,
  SdfParamsPreprocessed,
  SdfPattern,
  UsedLayerFeatures,
} from "./designMaterialTypes";
import { DESIGN_MATERIAL_VERSION } from "./designMaterialTypes";
import { cloneDefaultMaterialParams } from "./designMaterialUtils";

const ALWAYS_APPLY_LATEST_MIGRATION = false; // If set to true will apply migrations even if material version is already the most recent one - can be useful during development.

export function getUsedLayerFeatures(
  materialParams: DesignMaterialParamsPreprocessed,
  disableSDF: boolean,
): UsedLayerFeatures {
  let useSDFMask = false;
  let useSDFRandom = false;
  let hasHeightLayers = false;
  for (let i = 0; i < materialParams.layers.length; i++) {
    const layerParams = materialParams.layers[i];
    if (layerParams.maskID === "SDFMask" && !disableSDF) {
      useSDFRandom = true;
      useSDFMask = true;
    }
    if (layerParams.useForHeight) {
      hasHeightLayers = true;
    }
  }
  return { useSDFMask, useSDFRandom, hasHeightLayers, useSDFMaskOrRandom: useSDFMask || useSDFRandom };
}

export function designMaterialLayersAreFlatColor(materialParams: DesignMaterialParamsPreprocessed): boolean {
  for (let i = 0; i < materialParams.layers.length; i++) {
    const layerParams = materialParams.layers[i];
    if (layerParams.strength > 0) {
      return false;
    }
  }
  return true;
}

export function usesSdf(materialParams: DesignMaterialParamsPreprocessed) {
  const { useSDFMask, useSDFRandom } = getUsedLayerFeatures(materialParams, false);
  return useSDFMask || useSDFRandom || !materialParams.sdf.common.maskOnly;
}

export function sdfPatternEnabled(sdfCommon: SdfCommonParams, patterns: SdfPattern[], patternIndex: number): boolean {
  return patterns[patternIndex].enabled && (patternIndex === 0 || sdfCommon.randomTilesMode !== "RandomSplit");
}

// Returns if compile-time settings match for the 4 non-random SDF patterns, so SIMD mode can be used.
export function sdfCompileTimeSettingsMatch(patterns: (SdfPattern | SdfPatternPreprocessed)[]): boolean {
  let prevRoundSides: DmRoundSidesMode | undefined = undefined;
  let match = true;
  for (let index = 0; index < patterns.length; ++index) {
    if (!("enabled" in patterns[index]) || (patterns[index] as SdfPattern).enabled) {
      const roundSidesMode = patterns[index].roundSidesMode;
      if (prevRoundSides !== undefined && roundSidesMode !== prevRoundSides) {
        match = false;
      }
      prevRoundSides = roundSidesMode;
    }
  }
  return match;
}

/**
 * Some parameter values conflict with "continuous" SDF shapes. Sanitize the parameters to be consistent.
 * @param sdfCommon Common SDF parameters.
 * @param pattern Pattern to process in place.
 */
export function sanitizeSdfContinuity(sdfCommon: Readonly<SdfCommonParams>, pattern: SdfPatternPreprocessed) {
  if (sdfCommon.randomTilesMode !== "None") {
    // Random tiles mode ignores the "continuous" flag.
    if (pattern.continuousX || pattern.continuousY) {
      pattern.continuousX = false;
      pattern.continuousY = false;
    }
    return;
  }
  if (pattern.continuousX || pattern.continuousY) {
    if (sdfCommon.slopeAxis === "Z" || pattern.continuousY) {
      pattern.slope = 0;
    }
    pattern.cutXfromY = false;
    if (
      pattern.roundSidesMode === "roundSides" ||
      pattern.roundSidesMode === "roundZ" ||
      (pattern.roundSidesMode === "roundX" && pattern.continuousY) ||
      (pattern.roundSidesMode === "roundY" && pattern.continuousX)
    ) {
      pattern.roundSidesMode = "none";
    }
    if (pattern.useBevels) {
      pattern.bevelDistanceZ = 0;
      if (pattern.continuousX) {
        pattern.bevelDistanceY = 0;
      }
      if (pattern.continuousY) {
        pattern.bevelDistanceX = 0;
      }
    }
  }
}

// Determines top-down bounding box width for an SDF cell, taking into account deformations applied.
export function cellTopDownBoundingBoxX(pattern: SdfPatternPreprocessed) {
  return pattern.xSize + (pattern.cutXfromY ? pattern.cutXfromYScale * pattern.ySize : 0);
}

export function cellTopDownBoundingBoxY(sdfCommon: SdfCommonParamsPreprocessed, pattern: SdfPatternPreprocessed) {
  return (
    pattern.ySize +
    (sdfCommon.slopeAxis === "X" ? pattern.zSize * Math.abs(Math.sin(degreeToRadian(pattern.slope))) : 0)
  );
}

export function getCellZBoundsRelativeToTop(
  sdfCommon: SdfCommonParamsPreprocessed,
  patterns: SdfPatternPreprocessed[],
): [number, number] {
  let minZ = sdfCommon.depth;
  let maxZ = 0;
  for (const pattern of patterns) {
    const varyHeightFactor = pattern.varyHeight ? pattern.varyHeightScale * 0.5 : 0.0;
    const slope = degreeToRadian(sdfCommon.slopeAxis === "X" ? pattern.slope : 0);
    const zOffsetFromTop = sdfCommon.depth - pattern.zSize;
    minZ = Math.min(minZ, zOffsetFromTop - varyHeightFactor);
    maxZ = Math.max(
      maxZ,
      zOffsetFromTop + varyHeightFactor + Math.sin(Math.abs(slope)) * pattern.ySize + Math.cos(slope) * pattern.zSize,
    );
  }
  return [minZ, maxZ];
}

// Takes two hex strings in the format "#RRGGBB" and returns another one where the colors have been interpolated in linear space with weight t given to colorB.
export function mixSRGBColorStrings(colorA: string, colorB: string, t: number): string {
  const colorALinear = new Color(colorA);
  const colorBLinear = new Color(colorB);
  colorALinear.lerp(colorBLinear, t);
  return "#" + colorALinear.getHexString(SRGBColorSpace);
}

export function totalLayerInfluence(layerParams: LayerParams): number {
  const channelInfluence =
    layerParams.colorInfluence +
    layerParams.roughnessInfluence +
    layerParams.metalnessInfluence +
    (layerParams.useForHeight ? layerParams.heightInfluence : 0);
  return layerParams.strength * channelInfluence;
}

export function sdfGridToUvTransform(pattern: SdfPatternPreprocessed, rotate: boolean): Mat3 {
  const matScale: Mat3 = mat3Scale(pattern.xDistance, pattern.yDistance);
  const matTranslate: Mat3 = mat3Translate(-pattern.xOffset, -pattern.yOffset);
  const matTranslateScale: Mat3 = mat3Mult(matTranslate, matScale);
  if (!rotate) {
    return matTranslateScale;
  }
  const matRotate: Mat3 = mat3Rotate(degreeToRadian(pattern.rotation));
  const mat: Mat3 = mat3Mult(matRotate, matTranslateScale);
  return mat;
}

export function uvToSdfGridTransform(pattern: SdfPatternPreprocessed): Mat3 {
  const matRotate: Mat3 = mat3Rotate(-degreeToRadian(pattern.rotation));
  const matScale: Mat3 = mat3Scale(1.0 / pattern.xDistance, 1.0 / pattern.yDistance);
  const matTranslate: Mat3 = mat3Translate(pattern.xOffset, pattern.yOffset);
  const matTranslateRotate: Mat3 = mat3Mult(matTranslate, matRotate);
  const mat: Mat3 = mat3Mult(matScale, matTranslateRotate);
  return mat;
}

function isNear(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.01;
}

export enum PeriodType {
  VALID,
  REVERTED_TO_MAX,
  REVERTED_TO_DEFAULT,
}

interface SdfPatternPeriodResult {
  xPeriod: number | undefined;
  yPeriod: number | undefined;
  mult: number;
  continuousWorldX: boolean;
  continuousWorldY: boolean;
  type: PeriodType;
}

// Compute repetition period for the SDF pattern along the x and y axises.
function sdfPatternPeriod(
  sdfCommon: SdfCommonParamsPreprocessed,
  pattern: SdfPatternPreprocessed,
): SdfPatternPeriodResult {
  let mult = 1;
  if (sdfCommon.randomTilesMode === "RandomSplit") {
    // The split orientations form a checkerboard pattern, so the grid repeat period must be divisible by 2.
    mult = 2;
  }

  if (pattern.rotation === 0 || isNear(Math.abs(pattern.rotation), 180)) {
    const continuousWorldX = pattern.continuousX;
    const continuousWorldY = pattern.continuousY;
    const xPeriod = continuousWorldX ? undefined : pattern.xDistance * mult;
    const yPeriod = continuousWorldY ? undefined : pattern.yDistance * mult;
    return { xPeriod, yPeriod, mult, continuousWorldX, continuousWorldY, type: PeriodType.VALID };
  } else if (isNear(Math.abs(pattern.rotation), 90)) {
    const continuousWorldX = pattern.continuousY;
    const continuousWorldY = pattern.continuousX;
    const xPeriod = continuousWorldX ? undefined : pattern.yDistance * mult;
    const yPeriod = continuousWorldY ? undefined : pattern.xDistance * mult;
    return { xPeriod, yPeriod, mult, continuousWorldX, continuousWorldY, type: PeriodType.VALID };
  } else if (isNear(Math.abs(pattern.rotation) % 90, 45) && pattern.xDistance === pattern.yDistance) {
    return {
      xPeriod: Math.SQRT2 * pattern.xDistance * mult,
      yPeriod: Math.SQRT2 * pattern.xDistance * mult,
      mult,
      continuousWorldX: false,
      continuousWorldY: false,
      type: PeriodType.VALID,
    };
  }
  // Fall back to xDistance/yDistance.
  return {
    xPeriod: pattern.xDistance * mult,
    yPeriod: pattern.yDistance * mult,
    mult,
    continuousWorldX: false,
    continuousWorldY: false,
    type: PeriodType.REVERTED_TO_DEFAULT,
  };
}

interface CommonPeriod {
  period?: number;
  type: PeriodType;
}

function findApproximateCommonPeriod(periods: (number | undefined)[]): CommonPeriod {
  if (periods.length === 0) {
    return { period: 1, type: PeriodType.REVERTED_TO_DEFAULT };
  }
  if (periods.length === 1) {
    return { period: periods[0], type: PeriodType.VALID };
  }
  let minPeriod = periods[0];
  let maxPeriod = periods[0];
  for (const period of periods) {
    if (minPeriod === undefined || maxPeriod === undefined) {
      minPeriod = period;
      maxPeriod = period;
    } else if (period !== undefined) {
      minPeriod = Math.min(minPeriod, period);
      maxPeriod = Math.max(maxPeriod, period);
    }
  }
  if (minPeriod === undefined || maxPeriod === undefined) {
    return { period: undefined, type: PeriodType.VALID };
  }
  // Get approximate lowest common multiple of the different periods by doing a brute force search.
  const maxToTest = Math.max(1, Math.floor((20 * minPeriod) / maxPeriod));
  for (let i = 1; i <= maxToTest; ++i) {
    const lcmCandidate = maxPeriod * i;
    let lcmFound = true;
    for (const period of periods) {
      if (period === undefined) {
        continue;
      }
      if (lcmCandidate / period < 100.0 && lcmCandidate % period > period * 0.02) {
        lcmFound = false;
      }
    }
    if (lcmFound) {
      return { period: lcmCandidate, type: PeriodType.VALID };
    }
  }
  // Fall back to max period.
  return { period: maxPeriod, type: PeriodType.REVERTED_TO_MAX };
}

export enum SdfPeriodPurpose {
  MinimumPeriod,
  HeightMapPeriod,
  HatchPeriod, // Prefer longer lines for analytical rendering
  HatchTexturePeriod, // Prefer square aspect ratio and smaller size for 2D linework SDF texture rendering
}

export interface SdfPeriodResult {
  repeatPeriodX?: number;
  repeatPeriodY?: number;
  type: PeriodType;
}

// Compute the repetition period for a combination of SDF patterns along the x and y axes.
export function sdfPeriod(
  sdfCommon: SdfCommonParamsPreprocessed,
  sdfPatterns: SdfPatternPreprocessed[],
  purpose: SdfPeriodPurpose,
): SdfPeriodResult {
  const periods = sdfPatterns.map((pattern) => sdfPatternPeriod(sdfCommon, pattern));
  const xPeriods = periods.map((period) => period.xPeriod);
  const yPeriods = periods.map((period) => period.yPeriod);

  const allContinuousX = periods.every((pattern) => pattern.continuousWorldX);
  const allContinuousY = periods.every((pattern) => pattern.continuousWorldY);

  const { period: commonRepeatPeriodX, type: xType } = findApproximateCommonPeriod(xPeriods);
  const { period: commonRepeatPeriodY, type: yType } = findApproximateCommonPeriod(yPeriods);

  let type = PeriodType.VALID;
  if (xType === PeriodType.REVERTED_TO_MAX || yType === PeriodType.REVERTED_TO_MAX) {
    console.warn("SDF periodicity analysis: could not determine repeat period.");
    type = PeriodType.REVERTED_TO_MAX;
  }

  let minMult = Infinity;
  for (const period of periods) {
    minMult = Math.min(minMult, period.mult);
    if (type === PeriodType.VALID && period.type === PeriodType.REVERTED_TO_DEFAULT) {
      console.warn("SDF periodicity analysis: could not determine repeat period of a single rotated pattern.");
      type = PeriodType.REVERTED_TO_DEFAULT;
    }
  }

  let maxVaryHeightScale = 0;
  let maxDistance = 0;
  let minDistance = Infinity;
  for (const pattern of sdfPatterns) {
    maxVaryHeightScale = Math.max(maxVaryHeightScale, pattern.varyHeight ? pattern.varyHeightScale : 0.0);
    if (!pattern.continuousX) {
      maxDistance = Math.max(maxDistance, pattern.xDistance);
      minDistance = Math.min(minDistance, pattern.xDistance);
    }
    if (!pattern.continuousY) {
      maxDistance = Math.max(maxDistance, pattern.yDistance);
      minDistance = Math.min(minDistance, pattern.yDistance);
    }
  }

  let repeatPeriodX = commonRepeatPeriodX;
  let repeatPeriodY = commonRepeatPeriodY;
  if (repeatPeriodX === undefined) {
    repeatPeriodX = repeatPeriodY;
    if (allContinuousX && purpose === SdfPeriodPurpose.HatchPeriod) {
      repeatPeriodX = CONTINUOUS_HATCH_PATTERN_PERIOD;
    }
  } else if (repeatPeriodY === undefined) {
    repeatPeriodY = repeatPeriodX;
    if (allContinuousY && purpose === SdfPeriodPurpose.HatchPeriod) {
      repeatPeriodY = CONTINUOUS_HATCH_PATTERN_PERIOD;
    }
  }

  let repeatMultiplierX = 1;
  let repeatMultiplierY = 1;
  if (purpose !== SdfPeriodPurpose.MinimumPeriod) {
    if (
      maxVaryHeightScale > 0.0 ||
      sdfCommon.randomTilesMode === "RandomSplit" ||
      purpose === SdfPeriodPurpose.HeightMapPeriod
    ) {
      let desiredRepeatCell = maxDistance > 8 ? 4 : 8;
      if (sdfPatterns.length > 1) {
        desiredRepeatCell = 4;
      }
      if (sdfPatterns.length >= 6 && maxDistance > 20) {
        desiredRepeatCell = 2;
      }
      if (maxDistance > minDistance * 10 && maxDistance > 40) {
        // If there's some pattern that repeats on very large intervals compared to the other patterns, don't require as much repetition.
        desiredRepeatCell = 1;
      }
      // minMult tells how many cell instances we're already including in the repeat period.
      const desiredRepeatMultiplier = Math.ceil(desiredRepeatCell / minMult);
      let desiredRepeatMultiplierX = desiredRepeatMultiplier;
      let desiredRepeatMultiplierY = desiredRepeatMultiplier;

      if (purpose === SdfPeriodPurpose.HeightMapPeriod && repeatPeriodX !== undefined && repeatPeriodY !== undefined) {
        // Make the repeat period closer to square shaped.
        if (repeatPeriodX > repeatPeriodY * 2) {
          desiredRepeatMultiplierY *= Math.floor(repeatPeriodX / repeatPeriodY);
        } else if (repeatPeriodY > repeatPeriodX * 2) {
          desiredRepeatMultiplierX *= Math.floor(repeatPeriodY / repeatPeriodX);
        }

        const maxRepeatMultiplierX = Math.ceil(100 / repeatPeriodX);
        const maxRepeatMultiplierY = Math.ceil(100 / repeatPeriodY);
        repeatMultiplierX = Math.min(desiredRepeatMultiplierX, maxRepeatMultiplierX);
        repeatMultiplierY = Math.min(desiredRepeatMultiplierY, maxRepeatMultiplierY);
      } else {
        repeatMultiplierX = desiredRepeatMultiplierX;
        repeatMultiplierY = desiredRepeatMultiplierY;
      }
    }
  }

  // We're using textures that have power of two sizes. Avoid regular quantization errors by changing the repeat multipliers to powers of two.
  repeatMultiplierX = closestIntegerPowerOfTwo(repeatMultiplierX, -0.3);
  repeatMultiplierY = closestIntegerPowerOfTwo(repeatMultiplierY, -0.3);

  if (repeatPeriodX !== undefined) {
    repeatPeriodX *= repeatMultiplierX;
  }
  if (repeatPeriodY !== undefined) {
    repeatPeriodY *= repeatMultiplierY;
  }
  return { repeatPeriodX, repeatPeriodY, type };
}

export function getMinFeatureSize(sdf: SdfParamsPreprocessed): number {
  let minFeatureSize = Infinity;
  const isRandomSplit = sdf.common.randomTilesMode === "RandomSplit";
  for (const pattern of sdf.patterns) {
    if (isRandomSplit || !pattern.continuousX) {
      const xGap = pattern.xDistance - pattern.xSize;
      if (xGap > 0.01) {
        minFeatureSize = Math.min(minFeatureSize, xGap);
      }
    }
    if (isRandomSplit || !pattern.continuousY) {
      const yGap = pattern.yDistance - pattern.ySize;
      if (yGap > 0.01) {
        minFeatureSize = Math.min(minFeatureSize, yGap);
      }
    }
    if (isRandomSplit) {
      minFeatureSize = Math.min(minFeatureSize, pattern.xSize * 0.1);
      minFeatureSize = Math.min(minFeatureSize, pattern.ySize * 0.1);
    } else {
      if (!pattern.continuousX) {
        minFeatureSize = Math.min(minFeatureSize, pattern.xSize);
      }
      if (!pattern.continuousY) {
        minFeatureSize = Math.min(minFeatureSize, pattern.ySize);
      }
    }
  }
  if (!isRandomSplit) {
    // Check for minimum feature size coming from gaps between different SDF patterns that are aligned with each other.

    function getMinGapBetweenPatterns(
      offsetA: number,
      sizeA: number,
      distanceA: number,
      offsetB: number,
      sizeB: number,
      distanceB: number,
    ) {
      if (distanceA !== distanceB) {
        return undefined;
      }
      let offsetBNorm = fmod(offsetB, distanceA);
      let offsetANorm = fmod(offsetA, distanceA);
      if (offsetANorm > offsetBNorm) {
        // Ensure that the pattern with the greater offset is "B"
        let temp = offsetANorm;
        offsetANorm = offsetBNorm;
        offsetBNorm = temp;
        temp = sizeA;
        sizeA = sizeB;
        sizeB = temp;
      }

      let minGapBetween = Infinity;
      const gapBetweenPatternsA = offsetBNorm - sizeB * 0.5 - (offsetANorm + sizeA * 0.5);
      if (gapBetweenPatternsA > 0.01) {
        minGapBetween = Math.min(minGapBetween, gapBetweenPatternsA);
      }
      const gapBetweenPatternsB = offsetANorm - sizeA * 0.5 + distanceA - (offsetBNorm + sizeB * 0.5);
      if (gapBetweenPatternsB > 0.01) {
        minGapBetween = Math.min(minGapBetween, gapBetweenPatternsB);
      }
      return minGapBetween;
    }

    for (const patternA of sdf.patterns) {
      if (Math.abs(patternA.slope) > 4.0) {
        continue;
      }
      for (const patternB of sdf.patterns) {
        if (patternB === patternA) {
          continue;
        }
        if (Math.abs(patternB.slope) > 4.0) {
          continue;
        }
        if (patternA.rotation !== patternB.rotation) {
          continue;
        }
        if (patternA.zSize !== patternB.zSize) {
          continue;
        }
        if (!patternA.continuousX && !patternB.continuousX) {
          const minGapBetween = getMinGapBetweenPatterns(
            patternA.xOffset,
            patternA.xSize,
            patternA.xDistance,
            patternB.xOffset,
            patternB.xSize,
            patternB.xDistance,
          );
          if (minGapBetween !== undefined) {
            minFeatureSize = Math.min(minFeatureSize, minGapBetween);
          }
        }
        if (!patternA.continuousY && !patternB.continuousY) {
          const minGapBetween = getMinGapBetweenPatterns(
            patternA.yOffset,
            patternA.ySize,
            patternA.yDistance,
            patternB.yOffset,
            patternB.ySize,
            patternB.yDistance,
          );
          if (minGapBetween !== undefined) {
            minFeatureSize = Math.min(minFeatureSize, minGapBetween);
          }
        }
      }
    }
  }
  return minFeatureSize;
}

export function hasSdfDistortions(distortions: SdfDistortion[]): boolean {
  for (let i = 0; i < distortions.length; ++i) {
    if (distortions[i].enabled) {
      return true;
    }
  }
  return false;
}

export function preprocessMaterialParams(params: DesignMaterialParams): DesignMaterialParamsPreprocessed {
  const {
    rotateAllLayers,
    normalMapRotation: normalMapRotationIn,
    masks: masksIn,
    layers: layersIn,
    sdf: sdfIn,
    opacity: opacityIn,
    transmission: transmissionIn,
    ...restParams
  } = params;

  const { rotateAllSdfPatterns, ...sdfCommon } = sdfIn.common;

  // Remove disabled SDF patterns and update sdfMaskPatterns bitmasks on layers to match.
  const disabledPatternIndices: number[] = [];
  for (let i = 0; i < sdfIn.patterns.length; i++) {
    if (!sdfPatternEnabled(sdfIn.common, sdfIn.patterns, i)) {
      disabledPatternIndices.push(i);
    }
  }
  const patterns: SdfPatternPreprocessed[] = sdfIn.patterns
    .filter((_, i) => !disabledPatternIndices.includes(i))
    .map(({ enabled, rotation, ...rest }) => ({ ...rest, rotation: rotation + rotateAllSdfPatterns }));

  for (const pattern of patterns) {
    sanitizeSdfContinuity(sdfIn.common, pattern);
  }

  const layers: LayerParamsPreprocessed[] = layersIn
    .map((layer, originalIndex) => ({ ...layer, originalIndex }))
    .filter((layer) => layer.enabled)
    .map(({ enabled, ...rest }) => ({ ...rest }));

  for (const layer of layers) {
    // Update sdf pattern masks according to the disabled patterns that were pruned.
    let offset = 0;
    for (const idx of disabledPatternIndices) {
      layer.sdfMaskPatterns = spliceBitmask(layer.sdfMaskPatterns, idx - offset);
      offset++;
    }

    if (sdfCommon.maskOnly) {
      layer.sdfMaskDepthOverlay = 0.0;
    }
  }

  const masks: MaskParams[] = masksIn.map((mask) => ({ ...mask }));
  let normalMapRotation = normalMapRotationIn;
  if (rotateAllLayers !== 0) {
    for (const layer of layers) {
      layer.rotate += rotateAllLayers;
    }
    for (const mask of masks) {
      mask.rotate += rotateAllLayers;
    }
    normalMapRotation += rotateAllLayers;
  }

  const opacity =
    params.transparency === "alphaTransparentDoNotUse" || params.transparency === "approximateClearGlass"
      ? opacityIn
      : 1;
  const transmission = params.transparency === "experimentalTransmissiveDoNotUse" ? transmissionIn : 0;

  const preprocessedParams: DesignMaterialParamsPreprocessed = {
    preprocessed: true,
    ...restParams,
    opacity,
    transmission,
    normalMapRotation,
    layers,
    masks,
    sdf: {
      preprocessed: true,
      ...sdfIn,
      common: sdfCommon,
      patterns,
    },
  };
  clampSDFPatternSize(preprocessedParams);
  return preprocessedParams;
}

/**
 * Extract a common rotation from a set of SDF patterns.
 * Expects preprocessed patterns (disabled patterns already removed).
 */
export function normalizeSdfPatternRotation(
  sdfCommon: SdfCommonParamsPreprocessed,
  patterns: SdfPatternPreprocessed[],
): { normalizedPatterns: SdfPatternPreprocessed[]; rotation: number | undefined } {
  const normalizedPatterns: SdfPatternPreprocessed[] = [];
  let rotation: number | undefined;
  for (let i = 0; i < patterns.length; ++i) {
    const patternParams: SdfPatternPreprocessed = { ...patterns[i] };
    const slopeRotation = sdfCommon.slopeAxis === "Z" && sdfCommon.randomTilesMode === "None" ? patternParams.slope : 0;
    let patternRotation = patternParams.rotation;
    if (patternParams.xDistance === patternParams.yDistance && isNear(Math.abs(slopeRotation), 45)) {
      // Aligning the cell edges with x/y axis can improve height map quality.
      // The condition ensures that we only do this when it keeps the sdf periodic.
      patternRotation += slopeRotation;
    }

    if (rotation === undefined) {
      rotation = patternRotation;
    }
    patternParams.rotation -= rotation;
    normalizedPatterns.push(patternParams);
  }
  return { normalizedPatterns, rotation };
}

export function initUVMatrixFromNormalMap(params: Omit<DesignMaterialParamsPreprocessed, "sdf">, uvMat: Matrix3) {
  uvMat.setUvTransform(
    params.normalMapTranslateU / params.normalMapScaleU,
    params.normalMapTranslateV / params.normalMapScaleV,
    1.0 / params.normalMapScaleU,
    1.0 / params.normalMapScaleV,
    degreeToRadian(params.normalMapRotation),
    0,
    0,
  );
}

export function extractLayerConfig(materialParams: DesignMaterialParams): LayerConfigParams {
  const layerConfig: LayerConfigParams = {
    type: "layerConfig",
    version: materialParams.version,
    color: materialParams.color,
    useLegacyGamma: materialParams.useLegacyGamma,
    layers: [],
    masks: [],
  };

  for (let i = 0; i < materialParams.layers.length; ++i) {
    const layer = Object.assign({}, materialParams.layers[i]);
    layerConfig.layers.push(layer);
  }
  for (let i = 0; i < materialParams.masks.length; ++i) {
    const mask = Object.assign({}, materialParams.masks[i]);
    layerConfig.masks.push(mask);
  }
  return layerConfig;
}

export function applyLayerConfig(targetMaterialParams: DesignMaterialParams, layerConfig: LayerConfigParams) {
  console.assert(
    targetMaterialParams.version === layerConfig.version ||
      designMaterialMigrations.find(
        (migration) =>
          schemaVersionLessThanOrEqualTo(layerConfig.version, migration.maxSchemaVersion) &&
          !migration.applyOnLayerConfigs,
      ) !== undefined,
  );
  targetMaterialParams.color = layerConfig.color;
  targetMaterialParams.useLegacyGamma = layerConfig.useLegacyGamma;
  for (let i = 0; i < targetMaterialParams.layers.length; ++i) {
    Object.assign(targetMaterialParams.layers[i], layerConfig.layers[i]);
  }
  for (let i = 0; i < targetMaterialParams.masks.length; ++i) {
    Object.assign(targetMaterialParams.masks[i], layerConfig.masks[i]);
  }
}

export function clampSDFPatternSize(params: DesignMaterialParams | DesignMaterialParamsPreprocessed) {
  // In random split mode we allow oversized tiles - that can be useful when using a very large rounding radius for example.
  // And in parallax map mode there's no issue with using oversized tiles.
  if (params.sdf.common.randomTilesMode !== "RandomSplit") {
    for (let i = 0; i < params.sdf.patterns.length; i++) {
      const pattern = params.sdf.patterns[i];
      pattern.xSize = Math.min(pattern.xSize, pattern.xDistance);
      pattern.ySize = Math.min(pattern.ySize, pattern.yDistance);
    }
  }
  for (const pattern of params.sdf.patterns) {
    clampSDFPatternZSize(pattern, params.sdf.common);
  }
}

export function clampSDFPatternZSize(
  pattern: SdfPattern | SdfPatternPreprocessed,
  sdfCommon: SdfCommonParams | SdfCommonParamsPreprocessed,
) {
  pattern.zSize = Math.min(pattern.zSize, sdfCommon.depth);
}

export function parseDesignMaterialParams(s: string): DesignMaterialParams | undefined {
  let res = JSON.parse(s);

  if (res.type && res.type !== "material") {
    console.error("Tried to parse a " + res.type + " file as a material file.");
    return undefined;
  }

  if (res.version !== DESIGN_MATERIAL_VERSION || ALWAYS_APPLY_LATEST_MIGRATION) {
    for (const migration of designMaterialMigrations) {
      if (
        schemaVersionLessThanOrEqualTo(res.version, migration.maxSchemaVersion) ||
        (ALWAYS_APPLY_LATEST_MIGRATION && migration === designMaterialMigrations[designMaterialMigrations.length - 1])
      ) {
        res = migration.apply(res);
      }
    }
  }

  if (res.layers.length > NUM_LAYERS) {
    throw new Error(`Shader exceeds max layer count of ${NUM_LAYERS}`);
  }

  res.compatibleColorLibraries = [...new Set(res.compatibleColorLibraries)].sort();
  res.compatibleLayerConfigLibraries = [...new Set(res.compatibleLayerConfigLibraries)].sort();

  clampSDFPatternSize(res);

  return res;
}

export function parseLayerConfig(s: string): LayerConfigParams | undefined {
  let res = JSON.parse(s);
  if (res.type !== "layerConfig") {
    console.error("Tried to parse a " + res.type + " file as a layer config file.");
    return undefined;
  }
  if (res.version !== DESIGN_MATERIAL_VERSION || ALWAYS_APPLY_LATEST_MIGRATION) {
    for (const migration of designMaterialMigrations) {
      if (
        migration.applyOnLayerConfigs &&
        (schemaVersionLessThanOrEqualTo(res.version, migration.maxSchemaVersion) ||
          (ALWAYS_APPLY_LATEST_MIGRATION &&
            migration === designMaterialMigrations[designMaterialMigrations.length - 1]))
      ) {
        res = migration.apply(res);
      }
    }
  }
  return res;
}

export function formatMaterialJSON(
  params: DesignMaterialParams | LayerConfigParams | LayerConfigLibraryParams | undefined,
): string {
  return JSON.stringify(params, undefined, "  ");
}

export function formatColorLibJSON(params: ColorLibraryParams): string {
  return JSON.stringify(params, Object.keys(params).sort(), "  ");
}

export function cloneDefaultLayerConfigParams() {
  return {
    ...defaultLayerConfigParams,

    layers: [
      { ...defaultLayerParams },
      { ...defaultLayerParams },
      { ...defaultLayerParams },
      { ...defaultLayerParams },
    ],
    masks: [{ ...defaultMaskParams }, { ...defaultMaskParams }, { ...defaultMaskParams }, { ...defaultMaskParams }],
  };
}

function simplePropsEqual(objA: any, objB: any, excludeProperties?: string[]) {
  for (const propKey of Object.keys(objA)) {
    const propType = typeof objA[propKey];
    if (excludeProperties?.includes(propKey)) {
      continue;
    }
    if (propType === "string" || propType === "number" || propType === "boolean") {
      if (objA[propKey] !== objB[propKey]) {
        return false;
      }
    }
  }
  return true;
}

export function designMaterialParamsEqual(
  materialA: DesignMaterialParams,
  materialB: DesignMaterialParams,

  // false should be used for editing materials, true should be used to figure out if the materials will render exactly the same way.
  ignoreDisabledProperties: boolean = false,
): boolean {
  if (!simplePropsEqual(materialA, materialB)) {
    return false;
  }
  if (!usesSdf(preprocessMaterialParams(materialA)) || !usesSdf(preprocessMaterialParams(materialB))) {
    if (materialA.sdf.common.maskOnly !== materialB.sdf.common.maskOnly) {
      return false;
    }
  } else {
    const ignoreSdfProperties: (keyof SdfCommonParams)[] = [];
    if (materialA.sdf.common.maskOnly && ignoreDisabledProperties) {
      ignoreSdfProperties.push("depth");
      ignoreSdfProperties.push("useShadows");
      ignoreSdfProperties.push("useAO");
      ignoreSdfProperties.push("thumbnailAoStrength");
      ignoreSdfProperties.push("thumbnailAoTolerance");
      ignoreSdfProperties.push("aoStrength");
      ignoreSdfProperties.push("aoTolerance");
    }
    if (!simplePropsEqual(materialA.sdf.common, materialB.sdf.common, ignoreSdfProperties)) {
      return false;
    }
    if (!simplePropsEqual(materialA.sdf.randomTiles, materialB.sdf.randomTiles)) {
      return false;
    }
    if (materialA.sdf.patterns.length !== materialB.sdf.patterns.length) {
      return false;
    }
    for (let i = 0; i < materialA.sdf.patterns.length; ++i) {
      if (!materialA.sdf.patterns[i].enabled && !materialB.sdf.patterns[i].enabled) {
        continue;
      }
      const ignoreSdfPatternProperties: (keyof SdfPattern)[] = [];
      if (ignoreDisabledProperties) {
        if (!materialA.sdf.patterns[i].varyHeight) {
          ignoreSdfPatternProperties.push("varyHeightScale");
        }
        if (materialA.sdf.patterns[i].continuousX) {
          ignoreSdfPatternProperties.push("xDistance");
          ignoreSdfPatternProperties.push("xSize");
        }
        if (materialA.sdf.patterns[i].continuousY) {
          ignoreSdfPatternProperties.push("yDistance");
          ignoreSdfPatternProperties.push("ySize");
        }
        if (materialA.sdf.patterns[i].roundSidesMode === "none") {
          ignoreSdfPatternProperties.push("roundStrength");
        }
        if (!materialA.sdf.patterns[i].useBevels) {
          ignoreSdfPatternProperties.push("bevelDistanceX");
          ignoreSdfPatternProperties.push("bevelDistanceY");
          ignoreSdfPatternProperties.push("bevelDistanceZ");
        }
        if (!materialA.sdf.patterns[i].cutXfromY) {
          ignoreSdfPatternProperties.push("cutXfromYScale");
        }
      }
      if (!simplePropsEqual(materialA.sdf.patterns[i], materialB.sdf.patterns[i], ignoreSdfPatternProperties)) {
        return false;
      }
    }
    if (materialA.sdf.distortions.length !== materialB.sdf.distortions.length) {
      return false;
    }
    for (let i = 0; i < materialA.sdf.distortions.length; ++i) {
      if (!materialA.sdf.distortions[i].enabled && !materialB.sdf.distortions[i].enabled) {
        continue;
      }
      if (!simplePropsEqual(materialA.sdf.distortions[i], materialB.sdf.distortions[i])) {
        return false;
      }
    }
  }

  for (let i = 0; i < NUM_LAYERS; ++i) {
    if (!materialA.layers[i].enabled && !materialB.layers[i].enabled) {
      continue;
    }
    const ignoreLayerProperties: (keyof LayerParams)[] = [];
    if (ignoreDisabledProperties) {
      if (!materialA.layers[i].useForHeight) {
        ignoreLayerProperties.push("heightInfluence");
      }
      if (materialA.layers[i].colorCount === 2) {
        ignoreLayerProperties.push("color2");
        ignoreLayerProperties.push("color3");
        ignoreLayerProperties.push("color2Pos");
        ignoreLayerProperties.push("color3Pos");
      } else if (materialA.layers[i].colorCount === 3) {
        ignoreLayerProperties.push("color3");
        ignoreLayerProperties.push("color3Pos");
      }
      if (materialA.layers[i].maskID === "none") {
        ignoreLayerProperties.push("maskUvTransformID");
      }
      if (materialA.layers[i].maskID !== "SDFMask") {
        ignoreLayerProperties.push("sdfMaskRandomness");
        ignoreLayerProperties.push("sdfMaskRandomAverage");
        ignoreLayerProperties.push("sdfMaskPatterns");
        ignoreLayerProperties.push("sdfMaskDepthOverlay");
      }
      if (!materialA.layers[i].enableMaskThreshold) {
        ignoreLayerProperties.push("maskThreshold");
      }
    }
    if (!simplePropsEqual(materialA.layers[i], materialB.layers[i], ignoreLayerProperties)) {
      return false;
    }
    const maskId = materialA.layers[i].maskID;
    if (maskId.startsWith("Mask")) {
      const maskIndex = parseInt(maskId.substring(4));
      if (!simplePropsEqual(materialA.masks[maskIndex], materialB.masks[maskIndex])) {
        return false;
      }
    }
  }

  return true;
}

function simplePropsDiff(objA: any, objB: any, prefix = ""): string[] {
  const diff: string[] = [];
  for (const propKey of Object.keys(objA)) {
    const propType = typeof objA[propKey];
    if (propType === "string" || propType === "number" || propType === "boolean") {
      if (objA[propKey] !== objB[propKey]) {
        diff.push(`${prefix}${propKey}: ${objA[propKey]} != ${objB[propKey]}`);
      }
    }
  }
  return diff;
}

export function designMaterialDiff(materialA: DesignMaterialParams, materialB: DesignMaterialParams): string[] {
  const diff: string[] = [];
  diff.push(...simplePropsDiff(materialA, materialB));
  if (!usesSdf(preprocessMaterialParams(materialA)) || !usesSdf(preprocessMaterialParams(materialB))) {
    if (materialA.sdf.common.maskOnly !== materialB.sdf.common.maskOnly) {
      diff.push(`sdf.maskOnly: ${materialA.sdf.common.maskOnly} != ${materialB.sdf.common.maskOnly}`);
    }
  } else {
    diff.push(...simplePropsDiff(materialA.sdf.common, materialB.sdf.common, "sdf.common."));
    diff.push(...simplePropsDiff(materialA.sdf.randomTiles, materialB.sdf.randomTiles, "sdf.randomTiles."));
    if (materialA.sdf.patterns.length !== materialB.sdf.patterns.length) {
      diff.push(`sdf.patterns.length: ${materialA.sdf.patterns.length} != ${materialB.sdf.patterns.length}`);
    }
    for (let i = 0; i < Math.min(materialA.sdf.patterns.length, materialB.sdf.patterns.length); ++i) {
      if (!materialA.sdf.patterns[i].enabled && !materialB.sdf.patterns[i].enabled) {
        continue;
      }
      diff.push(...simplePropsDiff(materialA.sdf.patterns[i], materialB.sdf.patterns[i], `sdf.patterns[${i}].`));
    }
    if (materialA.sdf.distortions.length !== materialB.sdf.distortions.length) {
      diff.push(`sdf.distortions.length: ${materialA.sdf.distortions.length} != ${materialB.sdf.distortions.length}`);
    }
    for (let i = 0; i < Math.min(materialA.sdf.distortions.length, materialB.sdf.distortions.length); ++i) {
      if (!materialA.sdf.distortions[i].enabled && !materialB.sdf.distortions[i].enabled) {
        continue;
      }
      diff.push(
        ...simplePropsDiff(materialA.sdf.distortions[i], materialB.sdf.distortions[i], `sdf.distortions[${i}].`),
      );
    }
  }
  for (let i = 0; i < NUM_LAYERS; ++i) {
    if (!materialA.layers[i].enabled && !materialB.layers[i].enabled) {
      continue;
    }
    diff.push(...simplePropsDiff(materialA.layers[i], materialB.layers[i], `layers[${i}].`));
    const maskId = materialA.layers[i].maskID;
    const maskIndexProcessed: number[] = [];
    if (maskId.startsWith("Mask")) {
      const maskIndex = parseInt(maskId.substring(4));
      if (maskIndexProcessed.indexOf(maskIndex) >= 0) {
        diff.push(...simplePropsDiff(materialA.masks[maskIndex], materialB.masks[maskIndex], `masks[${maskIndex}].`));
      }
      maskIndexProcessed.push(maskIndex);
    }
  }
  return diff;
}

/**
 * Compares types contained in a JSON object to a baseline object. The sample must contain everything that the baseline does, with identical JS types.
 * The object that is passed in may not contain cyclical references.
 * Assumes that arrays only contain one type of object.
 * Does not enforce array length.
 * @param baseline Baseline to compare against.
 * @param sample Object to compare.
 * @param objectName Object name prefix added to the returned messages.
 * @returns
 */
function compareTypesInObject(baseline: any, sample: any, objectName = ""): string[] | undefined {
  const diff: string[] = [];
  const baselineType = typeof baseline;
  if (baseline === null) {
    return undefined;
  }
  if (baselineType === "number" || baselineType === "string" || baselineType === "boolean") {
    if (typeof sample !== baselineType) {
      diff.push(`${objectName}: ${baselineType} != ${typeof sample}`);
    }
  } else if (Array.isArray(baseline)) {
    if (!Array.isArray(sample)) {
      diff.push("Should be an array but wasn't: " + objectName);
    } else if (baseline.length > 0 && typeof baseline[0] === "object") {
      for (let i = 0, l = sample.length; i < l; ++i) {
        const arrayItemDiff = compareTypesInObject(baseline[0], sample[i], `${objectName}[${i}]`);
        if (arrayItemDiff) {
          diff.push(...arrayItemDiff);
        }
      }
    }
  } else if (baselineType === "object") {
    for (const propKey of Object.keys(baseline)) {
      if (sample[propKey] === undefined) {
        diff.push("Undefined: " + objectName + "." + propKey);
      } else {
        const fieldDiff = compareTypesInObject(baseline[propKey], sample[propKey], objectName + "." + propKey);
        if (fieldDiff) {
          diff.push(...fieldDiff);
        }
      }
    }
    for (const propKey of Object.keys(sample)) {
      if (baseline[propKey] === undefined) {
        diff.push("Extra property: " + objectName + "." + propKey);
      }
    }
  }
  return diff;
}

export interface DesignMaterialValidateResult {
  valid: boolean;
  errorMsg: string;
}

export function validateDesignMaterialParams(params: DesignMaterialParams | undefined): DesignMaterialValidateResult {
  const baselineParams = cloneDefaultMaterialParams();
  const errors = compareTypesInObject(baselineParams, params);
  if (errors && errors.length > 0) {
    return { valid: false, errorMsg: errors.join("\n") };
  } else {
    return { valid: true, errorMsg: "" };
  }
}

export function validateLayerConfigParams(params: LayerConfigParams | undefined): DesignMaterialValidateResult {
  const baselineParams = cloneDefaultLayerConfigParams();
  const errors = compareTypesInObject(baselineParams, params);
  if (errors && errors.length > 0) {
    return { valid: false, errorMsg: errors.join("\n") };
  } else {
    return { valid: true, errorMsg: "" };
  }
}

export function sdfPatternsPopulateToMax(params: DesignMaterialParams) {
  while (params.sdf.patterns.length < MAX_SDF_PATTERNS) {
    params.sdf.patterns.push({ ...defaultSdfPattern });
  }
}

export function sdfPatternsListTrim(params: DesignMaterialParams): DesignMaterialParams {
  // Remove disabled SDF patterns from the end of the list. This is safe to do, since it won't affect indexing.
  while (params.sdf.patterns.length > 0 && !params.sdf.patterns[params.sdf.patterns.length - 1].enabled) {
    params.sdf.patterns.pop();
  }
  return params;
}

export type THREEMeshMaterial = MeshPhysicalMaterial | MeshStandardMaterial;
